import BubbleCommon, {
  aigcAdvancedSummaryTips,
  aigcGPTOptionsTips,
  TipsMapType,
} from '@op/components/BubbleCommon';
import React, { useMemo } from 'react';
import {  Form, Radio } from 'tea-component';
import { useIntl } from 'umi';
import { CONTACT_EMAIL } from '@op/constants';
import { gptOptionsShortTextMap, gptOptionsTextMap } from '../../constants';
import { GptOptionsType } from '../../types';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import classNames from 'classnames';

type GPTOptionsFormType = {
  remainder: number;
  total: number;
  gptOptions: GptOptionsType;
  setGptOptions: (value: GptOptionsType) => void;
  loading?: boolean;
  basicNearElement?: JSX.Element;
  advancedNearElement?: JSX.Element;
  titleTips?: TipsMapType;
  isDeepseek?: boolean;
  shortText?: boolean;
};

export function GPTOptions({
  remainder,
  total,
  gptOptions,
  setGptOptions,
  loading,
  basicNearElement,
  advancedNearElement,
  isDeepseek,
  shortText,
}: GPTOptionsFormType) {
  const { formatMessage } = useIntl();

  const { advancedTips, hasRemainder } = useMemo(() => {
    const hasRemainder = remainder > 0 || remainder === -1;
    let text: any = null;
    if (!hasRemainder && gptOptions === 'advanced') {
      text = (
        <span className="dbo-form-advanced-label-tips">
          {formatMessage(
            {
              id: 'Please try again tomorrow or contact {name} for more usage.',
            },
            {
              name: <i style={{ color: '#326FF0' }}>{CONTACT_EMAIL}</i>,
            },
          )}
        </span>
      );
    } else if (gptOptions === 'advanced') {
      text = (
        <span className="dbo-form-advanced-label-tips dbo-warning">
          {formatMessage({ id: 'Expected to wait for 0.5h~2h' })}
        </span>
      );
    }

    return { advancedTips: text, hasRemainder };
  }, [formatMessage, remainder, gptOptions]);

  const [tipsMap, disabled] = useMemo(() => {
    const disabled = !hasRemainder || loading;
    const tipsMap = aigcAdvancedSummaryTips(total);

    return [tipsMap, disabled];
  }, [hasRemainder, loading, isDeepseek, total]);

  const gptOptionsMap = shortText ? gptOptionsShortTextMap : gptOptionsTextMap;

  return (
    <Radio.Group
      value={gptOptions}
      onChange={(value: any) => {
        setGptOptions(value);
        buriedBtnPoint('GPT_Type', value);
      }}
      className={` ${!advancedTips ? '' : '--has-remainder'}`}
    >
      <Radio name="basic" className={classNames("radio-item", { 'dbo-active': gptOptions === 'basic' })}>
        {formatMessage({ id: gptOptionsMap.basic })}
        {basicNearElement}
      </Radio>

      <Radio
        name="advanced"
        className={classNames("radio-item-advanced",  { 'dbo-active': gptOptions === 'advanced' })}
        disabled={disabled}
      >
        <div className="dbo-form-advanced-label">
          <div className="dbo-form-advanced-label-main">
            <BubbleCommon showIcon={false} placement="top" tipsMap={tipsMap}>
              {formatMessage({ id: gptOptionsMap.advanced })}
              {remainder !== -1 && (
                <span
                  style={{
                    fontSize: '12px',
                    color: '#838697',
                    marginLeft: '4px',
                  }}
                >
                  {formatMessage(
                    {
                      id: `(Remaining system uses today: {remainder}/{total})`,
                    },
                    {
                      remainder: (
                        <i
                          style={{
                            color: remainder > 0 ? '#29B569' : '#EF5858',
                          }}
                        >
                          {remainder}
                        </i>
                      ),
                      total,
                    },
                  )}
                </span>
              )}
            </BubbleCommon>
            {advancedNearElement}
          </div>
          {advancedTips}
        </div>
      </Radio>
    </Radio.Group>
  );
}

export function GPTOptionsForm({
  titleTips = aigcGPTOptionsTips,
  topElement,
  hasAdvancedSummary = true,
  ...props
}: GPTOptionsFormType & {
  topElement?: JSX.Element;
  hasAdvancedSummary?: boolean;
}) {
  const { formatMessage } = useIntl();

  if (!hasAdvancedSummary) return null;

  return (
    <Form.Item
      label={
        <div className="item-label">
          {formatMessage({ id: 'Summary Options' })}
          <span style={{ verticalAlign: '-2px' }}>
            <BubbleCommon tipsMap={titleTips} placement="top"></BubbleCommon>
          </span>
        </div>
      }
      className="dbo-aigc-radio-wrapper"
    >
      {topElement}
      <GPTOptions {...props}></GPTOptions>
    </Form.Item>
  );
}
